import React, { ReactNode, useRef } from 'react'

import { IActionRulesSubLogicStatementValue } from '../../../actions/types'

import style from './LogicStatement.module.scss'
import useTheme from '../../theme/useTheme'
import {
  ESCAPE_LOGIC_END_PLACEHOLDER,
  ESCAPE_LOGIC_START_PLACEHOLDER
} from '@aytm/app/swidget/base/modules/preview/components/const'
import { unescapeText } from '@aytm/app/swidget/base/modules/preview/components/escape'
import LogicRef from '@aytm/app/swidget/base/modules/preview/components/LogicRef'

export interface ILogicStatementProps {
  statement: string
  value: IActionRulesSubLogicStatementValue
}

const LogicStatement = React.memo<ILogicStatementProps>(
  ({ statement, value }) => {
    const theme = useTheme()
    const logicRef = useRef()

    const result: ReactNode[] = []
    let escapedText = statement
    if (!value.invalid && value.variables) {
      Object.entries(value.variables).forEach(entry => {
        let val = entry[0]
        if ('t' == val[val.length - 1] && /\d/.test(val[val.length - 2]))
          val = val.slice(0, Math.max(0, val.length - 1))
        const re = new RegExp(`\\b(${val})\\b`, 'gi')
        escapedText = escapedText.replace(re, `${ESCAPE_LOGIC_START_PLACEHOLDER}${val}${ESCAPE_LOGIC_END_PLACEHOLDER}`)
      })
    }
    const re = new RegExp(
      `${ESCAPE_LOGIC_START_PLACEHOLDER}[^${ESCAPE_LOGIC_END_PLACEHOLDER}]*${ESCAPE_LOGIC_END_PLACEHOLDER}`,
      'g'
    )

    let match = re.exec(escapedText)
    let position = 0

    if (match) {
      while (match) {
        const logicStatement = match[0]
        const logicStart = match.index
        const logicEnd = logicStart + logicStatement.length
        const textBefore = escapedText.slice(position, logicStart)
        const textAfter = escapedText.slice(logicEnd)
        if (textBefore) {
          result.push(<span key={position} dangerouslySetInnerHTML={{ __html: unescapeText(textBefore) }} />)
        }
        const refStatement = unescapeText(
          logicStatement.replace(
            new RegExp(`[${ESCAPE_LOGIC_START_PLACEHOLDER}${ESCAPE_LOGIC_END_PLACEHOLDER}]`, 'g'),
            ''
          )
        )

        result.push(
          <LogicRef
            key={logicStart}
            statement={refStatement}
            value={
              void 0 === value.variables[refStatement]
                ? value.variables[`${refStatement}t`]
                : value.variables[refStatement]
            }
          />
        )
        position = logicEnd
        match = re.exec(escapedText)
        if (!match && textAfter) {
          result.push(<span key={logicEnd} dangerouslySetInnerHTML={{ __html: unescapeText(textAfter) }} />)
        }
      }
    } else {
      result.push(<span key={position} dangerouslySetInnerHTML={{ __html: unescapeText(escapedText) }} />)
    }

    return (
      <span>
        <span
          ref={logicRef}
          className={`${style.LogicStatement} ${value.invalid ? style.invalid : ''}`}
          style={{ color: theme.style.accentColor }}
        >
          {result}
        </span>
      </span>
    )
  },
  (prevProps, nextProps) => prevProps.statement === nextProps.statement && prevProps.value === nextProps.value
)

LogicStatement.displayName = 'LogicStatement'

export default LogicStatement
